import { useState } from "react";
import { MapPin, CheckCircle2, PhoneCall } from "lucide-react";
import { CITIES, ZIP_PREFIXES } from "../data/content";
import { SITE } from "../config/site";
import { useUrgent } from "./UrgentModal";

export function AreaChecker() {
  const [value, setValue] = useState("");
  const [result, setResult] = useState(null);
  const openUrgent = useUrgent();

  const check = (e) => {
    e.preventDefault();
    const q = value.trim().toLowerCase();
    if (!q) return;
    const cityHit = CITIES.find((c) => c.toLowerCase() === q || c.toLowerCase().startsWith(q));
    const zipHit = /^\d{5}$/.test(q) && ZIP_PREFIXES.includes(q.slice(0, 3));
    setResult(
      cityHit || zipHit
        ? { ok: true, place: cityHit || `ZIP ${q}` }
        : { ok: false, place: value.trim() }
    );
  };

  return (
    <div
      data-testid="area-checker"
      className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8"
    >
      <div className="flex items-center gap-3">
        <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-teal-50 text-teal-700">
          <MapPin className="h-5 w-5" />
        </span>
        <h3 className="font-display text-xl font-bold text-slate-900">Service Area Check</h3>
      </div>
      <p className="mt-3 text-sm leading-relaxed text-slate-600">
        Enter your Eastern Iowa city or ZIP code to check mobile coverage — we serve roughly a
        100-mile radius around Cedar Rapids and Iowa City.
      </p>
      <form onSubmit={check} className="mt-5 flex flex-col gap-3 sm:flex-row">
        <input
          data-testid="area-checker-input"
          list="area-cities"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="e.g. Cedar Rapids or 52402"
          className="w-full rounded-full border border-slate-300 bg-white px-5 py-3 text-sm text-slate-900 outline-none transition focus:border-teal-600 focus:ring-2 focus:ring-teal-500/40"
        />
        <datalist id="area-cities">
          {CITIES.map((c) => (
            <option key={c} value={c} />
          ))}
        </datalist>
        <button
          data-testid="area-checker-submit"
          type="submit"
          className="shrink-0 rounded-full bg-teal-700 px-6 py-3 text-sm font-bold text-white shadow-sm transition-all hover:-translate-y-0.5 hover:bg-teal-800"
        >
          Check Coverage
        </button>
      </form>
      {result && (
        <div
          data-testid="area-checker-result"
          className={`mt-5 flex items-start gap-3 rounded-2xl p-4 text-sm ${
            result.ok ? "bg-teal-50 text-teal-900" : "bg-amber-50 text-amber-900"
          }`}
        >
          {result.ok ? (
            <>
              <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-teal-700" />
              <span>
                <strong>{result.place}</strong> is inside our mobile service area. Call or text{" "}
                <button
                  data-testid="area-checker-urgent-link"
                  onClick={openUrgent}
                  className="font-bold underline underline-offset-2"
                >
                  {SITE.phoneDisplay}
                </button>{" "}
                and we will come to you.
              </span>
            </>
          ) : (
            <>
              <PhoneCall className="mt-0.5 h-5 w-5 shrink-0 text-amber-700" />
              <span>
                <strong>{result.place}</strong> may be near the edge of our range. We often travel
                further — call or text {SITE.phoneDisplay} and we will confirm availability.
              </span>
            </>
          )}
        </div>
      )}
    </div>
  );
}
